import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  hasIndusvivaAuth,
  indusvivaApi,
  type LiveLocation,
  type LiveProduct,
} from '../lib/indusvivaApi';
import { getDefaultIndusVivaLiveProducts } from '../data/indusvivaProducts';

const CART_KEY = 'ileafs_cart';
const LOCATION_KEY = 'ileafs_selected_location';
const LOCATIONS_KEY = 'ileafs_pincode_locations';
const CUSTOMER_KEY = 'ileafs_customer';
const PINCODE_KEY = 'ileafs_pincode';
const WISHLIST_KEY = 'ileafs_wishlist';

export type CartItem = LiveProduct & { quantity: number };

export type CustomerSession = {
  customer_id?: number | string;
  customer_user_id?: number | string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  verified: boolean;
};

type StoreContextValue = {
  pincode: string;
  location: LiveLocation | null;
  locations: LiveLocation[];
  products: LiveProduct[];
  productsLoading: boolean;
  cart: CartItem[];
  cartCount: number;
  cartTotal: number;
  wishlistIds: number[];
  wishlistCount: number;
  customer: CustomerSession | null;
  pincodeReady: boolean;
  showPincodeModal: boolean;
  showVerifyModal: boolean;
  verifyReturnTo: string;
  verifySwitchAccount: boolean;
  error: string | null;
  setShowPincodeModal: (v: boolean) => void;
  setShowVerifyModal: (v: boolean) => void;
  setVerifyReturnTo: (path: string) => void;
  setVerifySwitchAccount: (v: boolean) => void;
  selectPincode: (pincode: string, location: LiveLocation, locations?: LiveLocation[]) => Promise<void>;
  changePincode: () => void;
  addToCart: (product: LiveProduct, qty?: number) => void;
  updateQty: (productId: number, qty: number) => void;
  removeFromCart: (productId: number) => void;
  clearCart: () => void;
  toggleWishlist: (productId: number) => void;
  isWishlisted: (productId: number) => boolean;
  setCustomer: (c: CustomerSession | null) => void;
  openCartFlow: (returnTo?: string) => boolean;
  openAccountFlow: () => boolean;
  refreshProducts: () => Promise<void>;
};

const StoreContext = createContext<StoreContextValue | null>(null);

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [pincode, setPincode] = useState(() => localStorage.getItem(PINCODE_KEY) || '');
  const [location, setLocation] = useState<LiveLocation | null>(() =>
    readJson<LiveLocation | null>(LOCATION_KEY, null),
  );
  const [locations, setLocations] = useState<LiveLocation[]>(() =>
    readJson<LiveLocation[]>(LOCATIONS_KEY, []),
  );
  const [products, setProducts] = useState<LiveProduct[]>(() => getDefaultIndusVivaLiveProducts());
  const [productsLoading, setProductsLoading] = useState(false);
  const [cart, setCart] = useState<CartItem[]>(() => readJson<CartItem[]>(CART_KEY, []));
  const [wishlistIds, setWishlistIds] = useState<number[]>(() => readJson<number[]>(WISHLIST_KEY, []));
  const [customer, setCustomerState] = useState<CustomerSession | null>(() =>
    readJson<CustomerSession | null>(CUSTOMER_KEY, null),
  );
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyReturnTo, setVerifyReturnTo] = useState('/cart');
  const [verifySwitchAccount, setVerifySwitchAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pincodeReady = Boolean(location?.postalcode_id);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlistIds));
  }, [wishlistIds]);

  useEffect(() => {
    if (location) localStorage.setItem(LOCATION_KEY, JSON.stringify(location));
    else localStorage.removeItem(LOCATION_KEY);
  }, [location]);

  useEffect(() => {
    if (locations.length) localStorage.setItem(LOCATIONS_KEY, JSON.stringify(locations));
    else localStorage.removeItem(LOCATIONS_KEY);
  }, [locations]);

  useEffect(() => {
    if (pincode) localStorage.setItem(PINCODE_KEY, pincode);
  }, [pincode]);

  useEffect(() => {
    if (customer) localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
    else localStorage.removeItem(CUSTOMER_KEY);
  }, [customer]);

  // Boot: attempt session eligibility; if location cached, refresh live products
  useEffect(() => {
    void indusvivaApi.session().catch(() => {
      /* soft check */
    });
    if (!location) {
      return;
    }
    void (async () => {
      setProductsLoading(true);
      try {
        const data = await indusvivaApi.products({ postcodeId: location.postalcode_id });
        if (data.products && data.products.length > 0) {
          setProducts(data.products);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unable to load live products');
      } finally {
        setProductsLoading(false);
      }
    })();
  }, [location]);

  const selectPincode = useCallback(async (pin: string, loc: LiveLocation, locs?: LiveLocation[]) => {
    setError(null);
    setProductsLoading(true);
    try {
      if (Number(loc.is_service_available) !== 1) {
        throw new Error('Delivery is not available for this pincode. Try another nearby post office.');
      }
      const data = await indusvivaApi.products({ postcodeId: loc.postalcode_id });
      setPincode(pin);
      setLocation(loc);
      const nextLocs =
        locs && locs.length
          ? [...locs]
          : [loc];
      // Keep selected BO present even if list was partial
      if (!nextLocs.some((l) => l.postalcode_id === loc.postalcode_id)) {
        nextLocs.unshift(loc);
      }
      setLocations(nextLocs);
      setProducts(data.products);
      setShowPincodeModal(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to check delivery');
      throw e;
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const changePincode = useCallback(() => {
    setShowPincodeModal(true);
  }, []);

  const refreshProducts = useCallback(async () => {
    if (!location) return;
    setProductsLoading(true);
    try {
      const data = await indusvivaApi.products({ postcodeId: location.postalcode_id });
      setProducts(data.products);
    } finally {
      setProductsLoading(false);
    }
  }, [location]);

  const addToCart = useCallback((product: LiveProduct, qty = 1) => {
    setCart((prev) => {
      const idx = prev.findIndex((p) => p.id === product.id);
      if (idx === -1) return [...prev, { ...product, quantity: qty }];
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
      return next;
    });
  }, []);

  const updateQty = useCallback((productId: number, qty: number) => {
    setCart((prev) => {
      if (qty <= 0) return prev.filter((p) => p.id !== productId);
      return prev.map((p) => (p.id === productId ? { ...p, quantity: qty } : p));
    });
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    setCart((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const toggleWishlist = useCallback((productId: number) => {
    setWishlistIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  }, []);

  const isWishlisted = useCallback(
    (productId: number) => wishlistIds.includes(productId),
    [wishlistIds],
  );

  const setCustomer = useCallback((c: CustomerSession | null) => {
    setCustomerState(c);
  }, []);

  /** Returns true if cart can be viewed (verified). Otherwise opens verify modal. */
  const openCartFlow = useCallback((returnTo = '/cart') => {
    if (!pincodeReady) {
      setShowPincodeModal(true);
      return false;
    }
    if (!customer?.verified) {
      setVerifyReturnTo(returnTo);
      setShowVerifyModal(true);
      return false;
    }
    return true;
  }, [pincodeReady, customer]);

  /** Returns true if account/orders can be viewed. Otherwise opens verify modal. */
  const openAccountFlow = useCallback(() => {
    if (!customer?.verified || !customer.customer_id || !hasIndusvivaAuth()) {
      setVerifyReturnTo('/account');
      setShowVerifyModal(true);
      return false;
    }
    return true;
  }, [customer]);

  const cartCount = useMemo(() => cart.reduce((n, i) => n + i.quantity, 0), [cart]);
  const cartTotal = useMemo(
    () => cart.reduce((n, i) => n + i.selling_price * i.quantity, 0),
    [cart],
  );
  const wishlistCount = wishlistIds.length;

  const value: StoreContextValue = {
    pincode,
    location,
    locations,
    products,
    productsLoading,
    cart,
    cartCount,
    cartTotal,
    wishlistIds,
    wishlistCount,
    customer,
    pincodeReady,
    showPincodeModal,
    showVerifyModal,
    verifyReturnTo,
    verifySwitchAccount,
    error,
    setShowPincodeModal,
    setShowVerifyModal,
    setVerifyReturnTo,
    setVerifySwitchAccount,
    selectPincode,
    changePincode,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    toggleWishlist,
    isWishlisted,
    setCustomer,
    openCartFlow,
    openAccountFlow,
    refreshProducts,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
